import { useEffect, useCallback } from 'react';

/**
 * Hook for handling global keyboard shortcuts.
 *
 * @param {Object} shortcuts - Map of shortcut keys to handler functions
 * @param {Object} options - Configuration options
 * @param {boolean} options.enabled - Whether shortcuts are enabled (default: true)
 * @param {boolean} options.preventDefault - Whether to prevent default behavior (default: true)
 *
 * @example
 * useKeyboardShortcuts({
 *   'n': () => openNewTaskModal(),
 *   'i': () => navigateToInbox(),
 *   '/': () => focusSearch(),
 * });
 */
export function useKeyboardShortcuts(shortcuts, options = {}) {
  const { enabled = true, preventDefault = true } = options;

  const handleKeyDown = useCallback((event) => {
    // Don't trigger shortcuts when typing in input fields
    const target = event.target;
    const isInputField =
      target.tagName === 'INPUT' ||
      target.tagName === 'TEXTAREA' ||
      target.tagName === 'SELECT' ||
      target.isContentEditable;

    if (isInputField) return;

    // Don't trigger if a modal is open (check for role="dialog")
    const modalOpen = document.querySelector('[role="dialog"]');
    if (modalOpen) return;

    // Build the shortcut key string
    const key = event.key.toLowerCase();
    const modifiers = [];
    if (event.metaKey || event.ctrlKey) modifiers.push('cmd');
    if (event.altKey) modifiers.push('alt');
    if (event.shiftKey) modifiers.push('shift');

    // Try with modifiers first (e.g., "cmd+n")
    let shortcutKey = modifiers.length > 0
      ? `${modifiers.join('+')}+${key}`
      : key;

    const handler = shortcuts[shortcutKey];

    if (handler && typeof handler === 'function') {
      if (preventDefault) {
        event.preventDefault();
      }
      handler(event);
    }
  }, [shortcuts, preventDefault]);

  useEffect(() => {
    if (!enabled) return;

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [enabled, handleKeyDown]);
}

/**
 * Common keyboard shortcut definitions.
 * These can be used as references when setting up shortcuts.
 */
export const SHORTCUT_KEYS = {
  NEW_TASK: 'n',
  INBOX: 'i',
  SEARCH: '/',
  TODAY: 't',
  SCHEDULE: 's',
  HABITS: 'h',
  GOALS: 'g',
  HELP: '?',
};

/**
 * Helper to display keyboard shortcut in UI
 * @param {string} key - The shortcut key
 * @returns {string} - Formatted shortcut for display
 */
export function formatShortcut(key) {
  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;

  return key
    .replace('cmd+', isMac ? '⌘' : 'Ctrl+')
    .replace('alt+', isMac ? '⌥' : 'Alt+')
    .replace('shift+', isMac ? '⇧' : 'Shift+')
    .toUpperCase();
}

export default useKeyboardShortcuts;
